<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { AlertTriangle, Info, X } from 'lucide-svelte';

  /**
   * Asking before something irreversible.
   *
   * # Why it is a component and not markup in the layout
   *
   * It was markup in the layout: a hundred and forty lines of dialog wired
   * directly to the mode switch, with the mode names interpolated into its
   * heading. The second thing that needed to ask a question — starting a new
   * selection, which now deletes the customer's uploaded list — could either
   * copy those hundred and forty lines or pretend to be a mode switch.
   *
   * So the shape moved here and the words became props. The mode switch passes
   * the same strings it always did and looks exactly as it did.
   *
   * # The copy is resolved by the caller
   *
   * `message` and `points` arrive already translated rather than as keys. The
   * mode switch interpolates two mode names into its message and the reset does
   * not, and a component that took keys would have to accept an untyped bag of
   * interpolation values to serve both.
   */

  interface Props {
    title: string;

    /**
     * Rendered as HTML, because the existing mode-switch copy carries emphasis
     * inside the sentence. Both callers pass strings from the locale files,
     * which are ours; nothing here is user input.
     */
    message: string;

    /** What will be lost, one line each. */
    points?: string[];

    /** The quieter line at the bottom — usually that it cannot be undone. */
    note?: string;

    confirmLabel: string;
    onConfirm: () => void;
    onCancel: () => void;
  }

  let { title, message, points = [], note, confirmLabel, onConfirm, onCancel }: Props = $props();

  /**
   * Escape cancels.
   *
   * The dialog stands between the customer and work they are about to lose, so
   * the way out has to be the one they will reach for without thinking. There
   * are three: the backdrop, the cross, and this.
   */
  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') onCancel();
  }
</script>

<svelte:window on:keydown={onKeydown} />

<div
  class="absolute inset-0 z-10 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm"
  role="presentation"
  onclick={onCancel}
>
  <!--
    The panel swallows the click that the backdrop uses to cancel. Without it,
    clicking anything inside the dialog dismisses it — including the confirm
    button, whose own handler then runs against a dialog already closing.
  -->
  <div
    class="mx-4 w-full max-w-md rounded-surface border border-hairline bg-white shadow-overlay"
    role="dialog"
    aria-modal="true"
    aria-label={title}
    tabindex="-1"
    onclick={(event) => event.stopPropagation()}
  >
    <div class="flex items-center justify-between border-b border-hairline px-5 py-4">
      <h3 class="flex items-center text-base font-semibold text-gray-900">
        <AlertTriangle class="mr-2 h-5 w-5 text-gray-600" />
        {title}
      </h3>
      <button
        type="button"
        onclick={onCancel}
        class="rounded-control p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
        title={$_('buttons.close')}
      >
        <X class="h-5 w-5" />
      </button>
    </div>

    <div class="px-5 py-4">
      <!-- eslint-disable-next-line svelte/no-at-html-tags -->
      <p class="mb-6 text-gray-700">{@html message}</p>

      {#if points.length > 0}
        <ul class="mb-6 space-y-2 text-sm text-gray-600">
          {#each points as point (point)}
            <li class="flex items-start space-x-2">
              <X class="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
              <span>{point}</span>
            </li>
          {/each}
        </ul>
      {/if}

      {#if note}
        <div class="rounded-control border border-hairline bg-surface-sunken p-3">
          <div class="flex items-center space-x-2 text-sm text-gray-600">
            <Info class="h-5 w-5 flex-shrink-0" />
            <span>{note}</span>
          </div>
        </div>
      {/if}
    </div>

    <div class="flex items-center justify-end gap-3 border-t border-hairline px-5 py-4">
      <button type="button" onclick={onCancel} class="btn btn-secondary">
        {$_('buttons.cancel')}
      </button>
      <button type="button" onclick={onConfirm} class="btn btn-primary">
        {confirmLabel}
      </button>
    </div>
  </div>
</div>
